/**
 * USI-010 (ADR-011 § Structural commitment #5; design doc § Per-kind UI):
 * per-kind body for the `missing-info-contribution` triage item.
 *
 * Per design doc § Per-kind UI a missing-info contribution has no picker —
 * just a small fill panel for whichever donor-info fields the backend flagged
 * as missing (a `MissingDonorField[]` set — name, occupation, employer,
 * and/or the four address inputs, rendered as the present subset). Two actions:
 *
 *   - **Save** (primary) — `PUT /api/finance/contacts/{contactId}` to
 *     write the filled-in occupation / employer onto the linked
 *     `FinanceContact`. Once both fields are non-null on the contact, the
 *     next `useSortInbox` refetch drops the row from the queue.
 *   - **"Mark as information requested"** (secondary, treasurer-first copy
 *     per `feedback_treasurer_first_copy.md`) — `POST /api/finance/contributions/{id}/donor-info-requested`
 *     (USI-010 backend). Writes one
 *     `FinanceAuditActions.DonorInfoRequested` audit row per design doc
 *     § Key decisions #4 (FPD-018 attestation preservation). Does NOT
 *     flip ledger status — the row stays Pending / Confirmed and the
 *     audit trail records that the treasurer asked the donor.
 *
 * On success either action invalidates the sort-inbox query (via the
 * mutation hook's `onSuccess`) and the modal advances chronologically per
 * ADR-011 commitment #11. A 409 ConcurrencyConflict from either path
 * surfaces an inline error and leaves the modal where it is.
 *
 * **Field-gating lives on the backend (AFU-001).** Occupation/employer are
 * amount-independent (always flagged when absent). The Address field is
 * threshold-gated server-side (AL-only, Ala. Code §17-5-8: a donor whose
 * calendar-year aggregate exceeds the itemization threshold). This body just
 * renders whichever subset the backend put in `item.missingFields` — no FE
 * threshold inference.
 *
 * Validation: Save is disabled when any required field is empty or
 * whitespace-only; Attest is always enabled. Per CONVENTIONS.md § USI-010
 * the keyboard letter is `R` for Request.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ApiFailure,
  updateFinanceContactRequest,
  ContactKind,
  DonorInfoRequestChannel,
  donorInfoRequestChannelPastLabel,
  missingFieldsLabel,
  type ContributionDto,
  type MissingDonorField,
  type TriageItem,
  ErrorCode,
  isStaleWrite,
} from '@signalvote/shared';
import { ContributorShapeBody } from './ContributorShapeBody';
import { DonorInfoChannelPicker } from '@/components/finance/shared';
import { Button } from '@/components/ui/button';
import { AddressSuggestInput } from '@/components/finance/shared/classification';
import {
  EMPTY_EMPLOYER_ADDRESS,
  EmployerAddressFields,
  employerAddressRequest,
} from '@/components/finance/contacts';
import { formatAmount, formatDate } from '@/components/finance/utils';
import { getDateStringInTimezone } from '@/lib/calendarUtils';
import { useCampaignStore } from '@/stores';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import {
  useUpdateFinanceContact,
  useDonorInfoRequested,
  useDonorInfoRequests,
  useConfirmContribution,
} from '@/hooks/queries';
import type { RegisterConfirmFn } from './PendingContributionBody';
import { financeFormInputClass } from '@/components/finance/shared/financeControlClasses';
import { readyProfile, profileUnreadyMessage } from '@/lib/complianceProfileRead';

/**
 * Callback the modal passes to register the body's R-key attestation
 * handler. Same shape as USI-009's `RegisterConfirmFn` but named for the
 * "Mark as information requested" action so the two registration slots in
 * `TriageModal` stay semantically distinct (Enter routes to confirm /
 * save; R routes to attestation).
 */
export type RegisterDonorInfoRequestedFn = (
  handler: { fn: () => void } | null,
) => void;

interface MissingInfoBodyProps {
  // ATL-001: the kind re-keyed to the entity (`Contribution`); the modal only
  // mounts this body for the `actionNeeded === 'ProvideDonorInfo'` sub-case,
  // where `missing` is always populated.
  item: Extract<TriageItem, { kind: 'Contribution' }>;
  /**
   * Contact's current `ConcurrencyVersion`. Optimistic concurrency on the
   * PUT path — pre-USI-010 the sort-inbox payload didn't surface contact
   * fields beside the contribution. The TriageModal wires this from
   * either a fetched FinanceContactDto or 0 as a defensive default; a
   * future server change can include the contact concurrency token in
   * the inbox payload directly.
   */
  contactConcurrencyVersion: number;
  /** Fired after a successful Save or attestation — advances the modal. */
  onClassified: () => void;
  /**
   * Registers / clears the body's Enter-key Save handler. Reuses
   * USI-009's `RegisterConfirmFn` type so the Enter-routing contract
   * stays single-sourced — `TriageModal`'s `bodyConfirm` slot accepts the
   * same shape from PendingContributionBody and MissingInfoBody alike.
   */
  onRegisterConfirm: RegisterConfirmFn;
  /** Registers / clears the body's R-key "Mark as requested" handler. */
  onRegisterRequestDonorInfo: RegisterDonorInfoRequestedFn;
}

export function MissingInfoBody({
  item,
  contactConcurrencyVersion,
  onClassified,
  onRegisterConfirm,
  onRegisterRequestDonorInfo,
}: MissingInfoBodyProps) {
  const contribution = item.contribution;
  // The jurisdiction word in the summary copy follows the campaign's compliance
  // profile (federal has no state display name → "Federal"). Reuses the same
  // `stateDisplayName` the ContactDetail donor-info surface reads.
  const profileRead = useComplianceProfile();
  const profile = readyProfile(profileRead);
  const profileUnready = profileUnreadyMessage(profileRead);
  const stateName = profile?.stateDisplayName ?? null;
  // Whether a recorded ask clears anything HERE. Declared per jurisdiction and served on the
  // compliance profile; undeclared coalesces to false server-side, so an unknown jurisdiction
  // simply isn't offered the affordance (ADR-078 — an offer declines rather than assumes).
  // Alabama declares no best-efforts policy: recording an ask there clears nothing, and an action
  // that reads as progress while achieving none is worse than no action at all.
  const bestEffortsAccepted = profile?.bestEffortsAccepted ?? false;
  // AFU-001: the backend-computed missing-fields SET drives which inputs render
  // (mirrors ExpenditureBody). The modal/ledger only mounts this body for
  // ProvideDonorInfo rows where the server always populates the set; default to
  // occupation + employer if it's somehow absent so the panel degrades to a
  // sensible form rather than an empty, immediately-saveable one.
  const fields = item.missingFields ?? ['Occupation', 'Employer'];
  const needsName = fields.includes('Name');
  const needsOccupation = fields.includes('Occupation') || fields.includes('VagueOccupation');
  const needsEmployer = fields.includes('Employer');
  const needsAddress = fields.includes('Address');
  const needsEmployerAddress = fields.includes('EmployerAddress');

  // A1: the donor name is only flagged when it is blank or still the import
  // placeholder ("Unknown donor"/"Unknown payee"), so we start the input empty
  // for the treasurer to type the real name rather than prefilling the
  // placeholder. Saved as the contact's organizationName (the import coerces a
  // single unsplit / nameless donor to an Organization).
  const [name, setName] = useState('');
  const [occupation, setOccupation] = useState('');
  const [employer, setEmployer] = useState('');
  // Employment location (VA COMET city/state of employment) rides the same
  // employment edit as employer. Free text, never gates Save (optional field).
  const [employmentLocation, setEmploymentLocation] = useState('');
  const [employerAddress, setEmployerAddress] = useState(EMPTY_EMPLOYER_ADDRESS);
  // AFU-001: address subset prefilled from the donor contact's current address
  // (mirrors ExpenditureBody's payee-address prefill).
  const [address, setAddress] = useState(contribution.contactAddress ?? '');
  // Optional suite/apt line — prefilled so a save never blanks an existing value;
  // never gates canSave (it's optional).
  const [addressLine2, setAddressLine2] = useState(contribution.contactAddress2 ?? '');
  const [city, setCity] = useState(contribution.contactCity ?? '');
  const [state, setState] = useState(contribution.contactState ?? '');
  const [zipCode, setZipCode] = useState(contribution.contactZipCode ?? '');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Issue #322 — the reclassify escape hatch. A donor stuck on this missing-info nag may be the
  // WRONG kind (e.g. a person imported as an org, or — the NC case — an individual nagged for
  // employer/occupation that is really an organization, which needs neither). Switching kind here
  // clears the wrong nag in place, without leaving for the contacts page or the lossy type-only
  // reclassify. A missing occupation/employer is individual-only, so that donor is switching TO an
  // organization; otherwise default the toggle to a person.
  const [reclassifying, setReclassifying] = useState(false);
  // How the request went out. There is no default: a channel the treasurer didn't choose would
  // be a fact they never asserted, and the obligations that read this turn on which one it was.
  const [channel, setChannel] = useState<DonorInfoRequestChannel | null>(null);
  // Campaign time, never browser time — a request sent at 02:00 UTC is not "yesterday"
  // because the treasurer happens to be reading it from California.
  const campaignTimeZoneId = useCampaignStore(
    (store) => store.selectedCampaign?.timeZoneId,
  );
  const switchToShape =
    needsOccupation || needsEmployer || needsEmployerAddress
      ? ContactKind.Organization
      : ContactKind.Individual;
  const switchToLabel = switchToShape === ContactKind.Organization ? 'an organization' : 'a person';

  const { mutateAsync: updateContactAsync, isPending: savePending } =
    useUpdateFinanceContact();
  const { mutateAsync: requestDonorInfoAsync, isPending: attestPending } =
    useDonorInfoRequested();
  // Every ask already on file for this gift — what tells the treasurer whether chasing again
  // is a first attempt or a fourth.
  const requestHistoryRead = useDonorInfoRequests(contribution.id);
  const requestHistory = requestHistoryRead.status === 'ready' ? requestHistoryRead.data : [];
  // Fill-and-confirm: when the treasurer fills the flagged fields on a
  // born-Pending imported contribution, that work IS the human sign-off — so
  // Save folds in the confirm and the row drops straight to Done, instead of
  // resurfacing as a redundant standalone Confirm. (The bare Confirm survives
  // only for untouched Pending rows, handled by PendingContributionBody.)
  const { mutateAsync: confirmContributionAsync, isPending: confirmPending } =
    useConfirmContribution();

  const isPending = savePending || attestPending || confirmPending;

  const canSave = useMemo(() => {
    if (needsName && name.trim() === '') return false;
    if (needsOccupation && occupation.trim() === '') return false;
    if (needsEmployer && employer.trim() === '') return false;
    if (needsEmployerAddress && Object.values(employerAddress).some((part) => part.trim() === '')) {
      return false;
    }
    // A full address (all four fields) is required to itemize the donor, so
    // Save stays disabled until the row would actually become Complete.
    if (
      needsAddress &&
      (address.trim() === '' ||
        city.trim() === '' ||
        state.trim() === '' ||
        zipCode.trim() === '')
    ) {
      return false;
    }
    return true;
  }, [
    needsName,
    needsOccupation,
    needsEmployer,
    needsAddress,
    needsEmployerAddress,
    name,
    occupation,
    employer,
    employerAddress,
    address,
    city,
    state,
    zipCode,
  ]);

  const handleSave = useCallback(async () => {
    setErrorMessage(null);
    let confirming = false;
    try {
      await updateContactAsync({
        row: { id: contribution.contactId, concurrencyVersion: contactConcurrencyVersion },
        data: updateFinanceContactRequest({
          // Only send the fields the inbox flagged as missing — a future
          // change to the missing-fields set doesn't force an expanding
          // payload on every save.
          // A1: a placeholder/blank donor name saves as organizationName (the
          // import models a single unsplit / nameless donor as an Organization,
          // so the PUT keeps it an Organization and recomposes the display name).
          ...(needsName ? { organizationName: name.trim() } : {}),
          ...(needsOccupation ? { occupation: occupation.trim() } : {}),
          ...(needsEmployer ? { employer: employer.trim() } : {}),
          // Employment location is not prefilled here (the missing-info form seeds it empty),
          // so only send it when the treasurer actually typed one — an untouched empty field
          // must not clobber an existing value.
          ...(employmentLocation.trim() ? { employmentLocation: employmentLocation.trim() } : {}),
          ...(needsEmployerAddress ? employerAddressRequest(employerAddress) : {}),
          ...(needsAddress
            ? {
                address: address.trim(),
                addressLine2: addressLine2.trim() || null,
                city: city.trim(),
                state: state.trim(),
                zipCode: zipCode.trim(),
              }
            : {}),
          concurrencyVersion: contactConcurrencyVersion,
        }),
      });
      // The contact PUT does not bump the contribution's version, so its own
      // token is still valid for the confirm. Only a still-Pending row needs
      // the flip; an already-Confirmed-but-incomplete row just became Complete
      // and drops to Done on the next refetch with no confirm call.
      if (contribution.ledgerStatus === 'Pending') {
        confirming = true;
        await confirmContributionAsync({
          row: contribution,
        });
      }
      onClassified();
    } catch (err) {
      // Branch on the stable error code per CONVENTIONS.md (see USI-009 /
      // USI-006 precedent). The backend's
      // `FinanceContactController.UpdateContact` 409 path carries
      // `code: "ConcurrencyConflict"`.
      if (
        !confirming && err instanceof ApiFailure && err.reason.kind === 'stale'
      ) {
        setErrorMessage(
          'This contact was updated elsewhere — refresh to see the latest.',
        );
        return;
      }
      // The info saved but the fold-in confirm lost a concurrency race — the
      // donor info IS persisted (durable), so this degrades gracefully to the
      // row resurfacing as a standalone Confirm.
      if (confirming && isStaleWrite(err)) {
        setErrorMessage(
          'Saved the donor info, but this contribution was updated elsewhere — refresh, then confirm it.',
        );
        return;
      }
      setErrorMessage(
        err instanceof Error ? err.message : 'Failed to update contact.',
      );
    }
  }, [
    updateContactAsync,
    confirmContributionAsync,
    contribution.contactId,
    contribution.id,
    contribution.ledgerStatus,
    contribution.concurrencyVersion,
    needsName,
    needsOccupation,
    needsEmployer,
    needsAddress,
    needsEmployerAddress,
    name,
    occupation,
    employer,
    employmentLocation,
    employerAddress,
    address,
    addressLine2,
    city,
    state,
    zipCode,
    contactConcurrencyVersion,
    onClassified,
  ]);

  const handleAttest = useCallback(async () => {
    setErrorMessage(null);
    // The button is disabled until a channel is picked; this guards the keyboard path,
    // which reaches the handler directly.
    if (!channel) {
      setErrorMessage('Pick how you asked the donor before recording the request.');
      return;
    }
    let confirming = false;
    try {
      const updated = await requestDonorInfoAsync({
        row: contribution,
        channel,
      });
      // Recording best efforts IS the sign-off (same as Save): the row files
      // with "Information Requested". Fold in the confirm for a still-Pending row
      // so it finishes in one gesture rather than resurfacing as a Confirm step.
      // Recording the request SPENDS the row's token, so the confirm must send the one the
      // server just handed back — the stale one would 409 against our own write.
      if (contribution.ledgerStatus === 'Pending') {
        confirming = true;
        await confirmContributionAsync({
          row: { id: contribution.id, concurrencyVersion: updated.concurrencyVersion },
        });
      }
      onClassified();
    } catch (err) {
      // `ErrorCode.DonorInfoRequestConcurrencyConflict` = "DonorInfoRequestConcurrencyConflict"
      // per USI-010 backend (AppConstants.Finance); mirrored in @signalvote/shared (USI-015).
      if (!confirming && isStaleWrite(err)) {
        setErrorMessage(
          'This contribution was updated elsewhere — refresh to see the latest.',
        );
        return;
      }
      // The request recorded but the fold-in confirm lost a race — best efforts
      // IS on file, so this degrades to the row resurfacing as a Confirm.
      if (confirming && isStaleWrite(err)) {
        setErrorMessage(
          'Recorded the request, but this contribution was updated elsewhere — refresh, then confirm it.',
        );
        return;
      }
      setErrorMessage(
        err instanceof Error
          ? err.message
          : 'Failed to record donor info request.',
      );
    }
  }, [
    requestDonorInfoAsync,
    confirmContributionAsync,
    channel,
    contribution.id,
    contribution.ledgerStatus,
    contribution.concurrencyVersion,
    onClassified,
  ]);

  // useRef stabilizes the handler identity across renders so the modal's
  // registration effect doesn't infinite-loop. Mirrors the USI-009
  // PendingContributionBody pattern — see that file for the load-bearing
  // rationale (effect → setState → re-render → new callback → effect →
  // infinite update).
  const handleSaveRef = useRef(handleSave);
  const handleAttestRef = useRef(handleAttest);
  useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);
  useEffect(() => {
    handleAttestRef.current = handleAttest;
  }, [handleAttest]);

  // Enter-key registration only when the form is valid AND no mutation is
  // in flight. The body de-registers itself otherwise so a half-filled
  // form doesn't fire Save on Enter.
  useEffect(() => {
    // In reclassify mode the fill form is hidden, so its Save must not fire on Enter.
    if (!canSave || isPending || reclassifying) {
      onRegisterConfirm(null);
      return;
    }
    onRegisterConfirm({ fn: () => handleSaveRef.current() });
    return () => onRegisterConfirm(null);
  }, [canSave, isPending, reclassifying, onRegisterConfirm]);

  // R-key registration whenever a mutation isn't in flight. Empty fields
  // are valid for the attest path (the AC: "validation rejects empty Save
  // but always allows empty Attest").
  useEffect(() => {
    if (isPending || reclassifying) {
      onRegisterRequestDonorInfo(null);
      return;
    }
    onRegisterRequestDonorInfo({ fn: () => handleAttestRef.current() });
    return () => onRegisterRequestDonorInfo(null);
  }, [isPending, reclassifying, onRegisterRequestDonorInfo]);

  // Issue #322 — reclassify mode: swap the fill form for the person/organization editor, which
  // writes the shared donor's kind + name + agreeing type atomically. All hooks above already
  // ran (they de-register their shortcuts while reclassifying), so this branch is render-only.
  if (reclassifying) {
    return (
      <div data-testid="missing-info-reclassify" className="flex flex-col gap-3">
        <ContributionSummary
          contribution={contribution}
          stateName={stateName}
          fields={fields}
          bestEffortsAccepted={bestEffortsAccepted}
        />
        <div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="missing-info-reclassify-back"
            onClick={() => setReclassifying(false)}
          >
            ← Back to donor info
          </Button>
        </div>
        <ContributorShapeBody
          item={item}
          contactConcurrencyVersion={contactConcurrencyVersion}
          onClassified={onClassified}
          initialShape={switchToShape}
        />
      </div>
    );
  }

  return (
    <div data-testid="missing-info-body" className="flex flex-col gap-4">
      {profileUnready && (
        <p role="alert" className="text-sm text-destructive">
          {profileUnready} Whether your state accepts best efforts comes from it.
        </p>
      )}
      <ContributionSummary
          contribution={contribution}
          stateName={stateName}
          fields={fields}
          bestEffortsAccepted={bestEffortsAccepted}
        />

      <div className="flex flex-col gap-3">
        {/*
          No autoFocus on either input: the keyboard hook treats a focused
          INPUT / TEXTAREA / SELECT as "typing into the form" and silently
          drops shortcut keys (so the treasurer can type 'r' into the
          occupation field without firing the R-key attestation). Mounting
          with focus inside the input would break the keyboard story — R
          would become a typed letter, Enter would have to bubble through
          the form to fire Save. The treasurer clicks into the field to
          type; the shortcuts work from the resting state of the modal.
        */}
        {needsName && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Donor name</span>
            <input
              data-testid="missing-info-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
              placeholder="e.g. Jane Q. Donor, Acme PAC"
            />
          </label>
        )}
        {needsOccupation && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Occupation</span>
            <input
              data-testid="missing-info-occupation"
              type="text"
              value={occupation}
              onChange={(e) => setOccupation(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
              placeholder="e.g. Engineer, Teacher, Retired"
            />
          </label>
        )}
        {needsEmployer && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Employer</span>
            <input
              data-testid="missing-info-employer"
              type="text"
              value={employer}
              onChange={(e) => setEmployer(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
              placeholder="e.g. Acme Corp, Self-employed, Not employed"
            />
          </label>
        )}
        {needsEmployer && (
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium">Employment location</span>
            <input
              data-testid="missing-info-employment-location"
              type="text"
              value={employmentLocation}
              onChange={(e) => setEmploymentLocation(e.target.value)}
              disabled={isPending}
              className={financeFormInputClass}
              placeholder="City, State"
            />
          </label>
        )}
        {needsEmployerAddress && (
          <div className="grid grid-cols-2 gap-3">
            <EmployerAddressFields
              idPrefix={`missing-info-${item.id}`}
              value={employerAddress}
              onChange={setEmployerAddress}
              labelClassName="text-sm font-medium"
              inputClassName={financeFormInputClass}
            />
          </div>
        )}
        {/*
          AFU-001 — the donor address subset, threshold-gated server-side
          (AL-only, Ala. Code §17-5-8: itemize a donor whose calendar-year
          aggregate exceeds the threshold with name + full address). Mirrors
          ExpenditureBody's payee-address inputs.
        */}
        {needsAddress && (
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Street address</span>
              <AddressSuggestInput
                data-testid="missing-info-address"
                value={address}
                disabled={isPending}
                className={financeFormInputClass}
                placeholder="e.g. 123 Main St"
                onChange={setAddress}
                onPick={(p) => {
                  setCity(p.city);
                  setState(p.state);
                  setZipCode(p.zip);
                }}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Address line 2</span>
              <input
                data-testid="missing-info-address2"
                type="text"
                value={addressLine2}
                onChange={(e) => setAddressLine2(e.target.value)}
                disabled={isPending}
                className={financeFormInputClass}
                placeholder="Apt, suite, floor (optional)"
              />
            </label>
            <div className="flex gap-3">
              <label className="flex flex-1 flex-col gap-1 text-sm">
                <span className="font-medium">City</span>
                <input
                  data-testid="missing-info-city"
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  disabled={isPending}
                  className={financeFormInputClass}
                  placeholder="e.g. Birmingham"
                />
              </label>
              <label className="flex w-20 flex-col gap-1 text-sm">
                <span className="font-medium">State</span>
                <input
                  data-testid="missing-info-state"
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  disabled={isPending}
                  className={financeFormInputClass}
                  placeholder="e.g. AL"
                />
              </label>
              <label className="flex w-28 flex-col gap-1 text-sm">
                <span className="font-medium">ZIP</span>
                <input
                  data-testid="missing-info-zip"
                  type="text"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  disabled={isPending}
                  className={financeFormInputClass}
                  placeholder="e.g. 35203"
                />
              </label>
            </div>
          </div>
        )}
      </div>

      {errorMessage && (
        <div
          role="alert"
          data-testid="missing-info-error"
          className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {errorMessage}
        </div>
      )}

      {/* Footer rule (shared with the other finance forms): the safe secondary
          ("Mark as information requested") is a quiet ghost link above the
          full-width primary. Save only writes the donor fields — it does NOT
          confirm the row — so the primary label stays "Save", not "Save & confirm". */}
      <div className="flex flex-col gap-2 border-t pt-3">
        {/* Every ask already on file. A treasurer deciding whether to chase again needs to see
            that they have chased three times already — a single "requested" flag couldn't say
            so, which is why each ask is recorded separately. Not gated on which field is
            outstanding: a gift chased last month still has that history when only its address
            is missing today. */}
        {(requestHistoryRead.status === 'failed' || requestHistoryRead.status === 'offline') && (
          <p role="alert" className="text-[11px] text-destructive">
            {requestHistoryRead.status === 'offline'
              ? "You're offline, so the requests already sent to this donor can't load."
              : "Couldn't load the requests already sent to this donor."}
          </p>
        )}
        {requestHistory.length > 0 && (
          <div
            data-testid="missing-info-request-history"
            className="rounded-md border bg-muted/40 px-3 py-2"
          >
            <p className="text-[11px] font-medium text-muted-foreground">
              {requestHistory.length === 1
                ? 'You asked this donor once:'
                : `You asked this donor ${requestHistory.length} times:`}
            </p>
            <ul className="mt-1 space-y-0.5">
              {requestHistory.map((request) => (
                <li
                  key={`${request.requestedAt}-${request.channel}`}
                  className="text-[11px] text-muted-foreground"
                >
                  {donorInfoRequestChannelPastLabel(request.channel)}{' '}
                  {formatDate(getDateStringInTimezone(request.requestedAt, campaignTimeZoneId))}
                </li>
              ))}
            </ul>
          </div>
        )}
        {bestEffortsAccepted && (needsOccupation || needsEmployer) && (
          <DonorInfoChannelPicker
            value={channel}
            onChange={setChannel}
            disabled={isPending}
            id="missing-info-channel"
            className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground"
          />
        )}
        {bestEffortsAccepted && (
          <div className="text-center">
            <Button
              type="button"
              variant="ghost"
              data-testid="missing-info-attest"
              disabled={isPending || !channel}
              onClick={handleAttest}
            >
              Mark as information requested
            </Button>
          </div>
        )}
        {/* Best-efforts explainer (research-grounded: 11 CFR 104.7). Federal
            itemization only — the "Information Requested" attest applies to
            occupation/employer, which a state filer (AL: name + address only)
            never has flagged. Gated on those fields so this FEC vocabulary never
            reaches a state surface (same jurisdiction-branch posture as
            LoansCard's Schedule-C copy). Worded so it doesn't read as a shortcut
            around actually asking. */}
        {bestEffortsAccepted && (needsOccupation || needsEmployer) && (
          <p
            data-testid="missing-info-best-efforts-note"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            &quot;Mark as information requested&quot; files occupation and employer as
            &quot;Information Requested&quot; under the FEC best-efforts rule (11 CFR
            104.7). Use it only once you&apos;ve actually asked the donor; it records
            when and how you asked, it isn&apos;t a way to skip it.
          </p>
        )}
        <Button
          type="button"
          className="w-full"
          data-testid="missing-info-save"
          disabled={isPending || !canSave}
          onClick={handleSave}
        >
          Save
        </Button>
        {/* Issue #322 — the escape hatch: this donor may simply be the wrong kind. Switching
            clears the wrong nag (e.g. an organization needs no employer/occupation) in place. */}
        {/* Only offered when switching would actually clear the nag. An employer/occupation nag is
            individual-only, so an organization filed as a person can escape it here. An ADDRESS is
            owed by both kinds, so switching clears nothing — and since the target kind was inferred
            from the missing fields rather than the contact's real kind, that case used to offer to
            turn a person into "a person". */}
        {(needsOccupation || needsEmployer) && (
          <div className="text-center">
            <Button
              type="button"
              variant="link"
              size="sm"
              data-testid="missing-info-reclassify-open"
              disabled={isPending}
              onClick={() => setReclassifying(true)}
            >
              Wrong kind of donor? This is actually {switchToLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ContributionSummary({
  contribution,
  stateName,
  fields,
  bestEffortsAccepted,
}: {
  contribution: ContributionDto;
  /** The campaign's state display name, or null for a federal filer. */
  stateName: string | null;
  /** What the server actually flagged — the sentence names THESE, never a fixed pair. */
  fields: MissingDonorField[];
  /** Whether "record that you asked" is an option here; the sentence only offers it if so. */
  bestEffortsAccepted: boolean;
}) {
  return (
    <div
      data-testid="missing-info-summary"
      className="rounded-md border bg-muted/30 p-3 text-sm"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-mono text-xs text-muted-foreground">
          {contribution.date}
        </span>
        <span className="font-semibold">{formatAmount(contribution.amount)}</span>
        <span className="text-foreground">{contribution.contactName}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {stateName ?? 'Federal'} filings need this donor&apos;s{' '}
        {missingFieldsLabel(fields)}. Add it below
        {bestEffortsAccepted ? ', or record that you asked (see below)' : ''}.
      </p>
    </div>
  );
}
